import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function LoginPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message || 'アカウント作成に失敗しました');
        } else {
          setSuccessMessage('アカウントが作成されました！ログインしてください。');
          setIsSignUp(false);
          setPassword('');
          setConfirmPassword('');
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError('メールアドレスまたはパスワードが正しくありません');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      setError('エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* ロゴ・タイトル */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-store-2-line text-3xl text-white"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Anoe's Eye
            </h1>
            <p className="text-sm text-gray-600">
              {isSignUp ? 'アカウントを作成' : 'ログイン'}
            </p>
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* 成功メッセージ */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {successMessage}
            </div>
          )}

          {/* フォーム */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メールアドレス
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="example@email.com"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                パスワード
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="6文字以上"
                disabled={loading}
              />
            </div>

            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  パスワード（確認）
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="もう一度入力"
                  disabled={loading}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-lg font-medium hover:from-teal-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  処理中...
                </span>
              ) : isSignUp ? (
                'アカウントを作成'
              ) : (
                'ログイン'
              )}
            </button>
          </form>

          {/* 切り替えリンク */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setSuccessMessage('');
              }}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              {isSignUp
                ? 'すでにアカウントをお持ちの方はこちら'
                : 'アカウントをお持ちでない方はこちら'}
            </button>
          </div>

          {/* デモボタン */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/demo')}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-eye-line mr-2"></i>
              デモを試す
            </button>
          </div>
        </div>

        {/* アプリ切り替えボタン */}
        <div className="mt-4 bg-white rounded-2xl shadow-xl p-4">
          <p className="text-xs font-medium text-gray-400 text-center mb-3 uppercase tracking-wide">アプリを切り替える</p>
          <div className="grid grid-cols-5 gap-1.5">
            <a
              href="https://dpwkld.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border border-teal-200 bg-teal-50 hover:bg-teal-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg shrink-0">
                <i className="ri-home-4-line text-white text-sm"></i>
              </div>
              <span className="text-xs font-medium text-teal-700 whitespace-nowrap">ホーム</span>
            </a>
            <a
              href="https://qrshme.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-emerald-500 rounded-lg shrink-0">
                <i className="ri-archive-stack-line text-white text-sm"></i>
              </div>
              <span className="text-xs font-medium text-emerald-700 whitespace-nowrap">棚卸し</span>
            </a>
            <a
              href="https://isjfrc.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border border-cyan-200 bg-cyan-50 hover:bg-cyan-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-cyan-500 rounded-lg shrink-0">
                <i className="ri-calendar-check-line text-white text-sm"></i>
              </div>
              <span className="text-xs font-medium text-cyan-700 whitespace-nowrap">月次</span>
            </a>
            <a
              href="https://xmkhwj.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border border-orange-200 bg-orange-50 hover:bg-orange-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-orange-400 rounded-lg shrink-0">
                <i className="ri-time-line text-white text-sm"></i>
              </div>
              <span className="text-xs font-medium text-orange-700 whitespace-nowrap">タイム</span>
            </a>
            <a
              href="https://uzbfni.readdy.co"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-rose-500 rounded-lg shrink-0">
                <i className="ri-bar-chart-2-line text-white text-sm"></i>
              </div>
              <span className="text-xs font-medium text-rose-700 whitespace-nowrap">売上</span>
            </a>
          </div>
        </div>

        {/* フッター */}
        <p className="text-center text-xs text-gray-500 mt-6">
          © 2025 Anoe's Eye. All rights reserved.
        </p>
      </div>
    </div>
  );
}